import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import LoadingBar from './LoadingBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [authState, setAuthState] = useState({
    isAuthenticated: location.state?.user ? true : null,
    user: location.state?.user || null,
    subscriptionBlocked: false,
    mustChangePassword: false
  });

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      // Retry transient failures (timeouts, 5xx, Cloudflare). Only an explicit 401
      // should kick the user out — anything else keeps them logged in.
      const attemptCheck = async (attempts = 3) => {
        let lastError = null;
        for (let i = 0; i < attempts; i++) {
          try {
            return await axios.get(`${BACKEND_URL}/api/auth/me`, {
              withCredentials: true,
              timeout: 12000,
            });
          } catch (err) {
            lastError = err;
            // Hard logout only on explicit 401 (invalid/expired session) — bail immediately
            if (err.response?.status === 401) throw err;
            // For 403, 5xx, timeouts, network errors → retry with backoff
            if (i < attempts - 1) {
              await new Promise(r => setTimeout(r, 500 * (i + 1)));
            }
          }
        }
        throw lastError;
      };

      try {
        const response = await attemptCheck();
        const user = response.data;
        
        if (user.role === 'admin') {
          setAuthState({
            isAuthenticated: true,
            user,
            subscriptionBlocked: false,
            mustChangePassword: false,
            isAdmin: true
          });
          return;
        }
        
        if (user.must_change_password) {
          setAuthState({
            isAuthenticated: true,
            user,
            subscriptionBlocked: false,
            mustChangePassword: true
          });
          return;
        }
        
        const isInactive = user.subscription_status === 'overdue' || 
                         user.subscription_status === 'cancelled' ||
                         user.is_expired;
        
        setAuthState({
          isAuthenticated: true,
          user: { ...user, subscription_inactive: isInactive },
          subscriptionBlocked: false,
          mustChangePassword: false
        });
      } catch (error) {
        const status = error.response?.status;
        if (status === 401) {
          // Real session failure — log out
          console.warn('Sessão expirada (401), redirecionando ao login');
          setAuthState({
            isAuthenticated: false,
            user: null,
            subscriptionBlocked: false,
            mustChangePassword: false
          });
        } else {
          // Transient failure (network, 5xx, ingress, Cloudflare). DO NOT log out —
          // mark optimistic-auth so the app renders and let pages re-attempt their loads.
          // This prevents the "deslogou do nada" UX when /auth/me hiccups.
          console.warn('Falha transitória em /auth/me — mantendo sessão:', status || error.message);
          setAuthState({
            isAuthenticated: true,
            user: { user_id: 'pending', email: '', name: '', role: 'user', subscription_status: 'active', _transient_error: true },
            subscriptionBlocked: false,
            mustChangePassword: false
          });
        }
      }
    };

    checkAuth();
  }, [location.state]);

  // Loading state
  if (authState.isAuthenticated === null) {
    return <LoadingBar text="Carregando..." />;
  }

  // Not authenticated
  if (!authState.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admin user trying to access user routes
  if (authState.isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  // Must change password
  if (authState.mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  // Subscription blocked
  if (authState.subscriptionBlocked) {
    return <Navigate to="/subscription-blocked" replace />;
  }

  return children;
};

export default ProtectedRoute;
